import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { getCurrentUser } from "@/lib/api/auth";
import { useForm } from "@/hooks/useForm";
import { profileSchema } from "@shared/validation/composites/profile-fields";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageNavigationHeader } from "@/components/ui/page-navigation-header";
import { Value, DataPair, Text } from "@/components/ui/text";
import { InputWrapper } from "@/components/ui/input-wrapper";
import { FormLabel } from "@/components/ui/form-label";
import { SettingsPageLayout } from "@/components/ui/settings-page-layout";
import { Heading } from "@/components/ui/heading";
import { FormGrid } from "@/components/ui/form-grid";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { Surface } from "@/components/ui/surface";
import { Icon } from "@/components/ui/icons";
import { useSizeClass } from "@/hooks/use-size-class";
import { useUserEntity } from "@/contexts/user-entity-context";
import { DirtyIndicator } from "@/components/ui/dirty-indicator";
import { TicketActivitySection } from "@/components/shared/ticket-activity-section";

// Section wrapper - uses Card and CardHeader
const ProfileSection = ({
  title,
  children,
  action,
  testId,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  testId?: string;
}) => {
  return (
    <Card padding="none" data-testid={testId}>
      <CardHeader>
        <Heading size="sm">{title}</Heading>
        {action}
      </CardHeader>
      <VStack gap="none" padding="lg">
        {children}
      </VStack>
    </Card>
  );
};

function ProfilePage() {
  const navigate = useNavigate();
  const { t: tAccount } = useTranslation("account");
  const { t: tCommon } = useTranslation("common");
  const { t: tDashboard } = useTranslation("dashboard");
  const { t: tNav } = useTranslation("nav");
  const { t: tProfile } = useTranslation("profile");
  const { t: tSettings } = useTranslation("settings");
  const { entity: userEntity } = useUserEntity();
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const isRegular = sizeClass === "regular";
  const user = getCurrentUser();
  const [isEditing, setIsEditing] = useState(false);
  const [pendingDiscard, setPendingDiscard] = useState<"back" | "edit" | null>(
    null,
  );

  const profileDefaults = { name: user?.name ?? "", phone: "" };

  const form = useForm({
    schema: profileSchema(),
    defaults: profileDefaults,
    onSubmit: async () => {
      setIsEditing(false);
    },
  });

  if (!user) {
    return null;
  }

  const canSave = isEditing && form.isValid && form.isDirty;

  const handleBack = () => {
    if (isEditing && form.isDirty) {
      setPendingDiscard("back");
      return;
    }
    void navigate({ to: "/account" });
  };

  const handleToggleEdit = () => {
    if (!isEditing) {
      form.reset(profileDefaults);
      setIsEditing(true);
      return;
    }

    if (form.isDirty) {
      setPendingDiscard("edit");
      return;
    }

    form.reset(profileDefaults);
    setIsEditing(false);
  };

  const handleDiscardConfirm = () => {
    const target = pendingDiscard;
    setPendingDiscard(null);
    if (target === "back") {
      void navigate({ to: "/account" });
    } else if (target === "edit") {
      form.reset(profileDefaults);
      setIsEditing(false);
    }
  };

  const handleSave = async () => {
    await form.submitSafe();
  };

  return (
    <VStack gap="xl" data-testid="profile-page">
      <PageNavigationHeader
        title={tNav("profile")}
        contextLabel={tSettings("account")}
        metadata={tAccount("profileDescription")}
        onBack={handleBack}
        backAriaLabel={tCommon("back")}
        contextAriaLabel={tSettings("account")}
      />

      {userEntity?.tickets ? (
        <TicketActivitySection tickets={userEntity.tickets} />
      ) : null}

      <SettingsPageLayout variant="profile">
        {/* eslint-disable-next-line design-tokens/no-raw-html-elements -- CSS grid layout with responsive column template */}
        <div
          data-testid="profile-layout"
          className="grid grid-cols-1 xl:grid-cols-3 gap-6"
        >
          {/* Left column - Profile card */}
          <VStack className="xl:col-span-1">
            <Card data-testid="profile-info-card" className="p-6 text-center">
              {/* Avatar */}
              <Surface
                bg="primary-container"
                rounded="full"
                className="flex w-24 h-24 items-center justify-center mx-auto mb-4"
              >
                <Heading
                  size="xl"
                  color="primary"
                  className="text-3xl font-bold"
                >
                  {user.name.charAt(0).toUpperCase()}
                </Heading>
              </Surface>

              <Value size="lg" weight="semibold" className="text-lg">
                {user.name}
              </Value>
              <Value color="muted" size="sm">
                {user.email}
              </Value>

              {user.entity && (
                <Badge
                  color="info"
                  className="mt-3 bg-[color:var(--primary-container)] dark:bg-[color:var(--primary-container)] text-[color:var(--primary)]"
                >
                  <Icon.Building size="sm" />
                  {user.entity.name}
                </Badge>
              )}

              <Button color="neutral" variant="outline" className="w-full mt-6">
                {tCommon("edit")}
              </Button>
            </Card>

            {/* Quick stats */}
            <Card data-testid="profile-stats-card" className="p-6 mt-6">
              <Value weight="semibold" className="mb-4">
                {tDashboard("overview")}
              </Value>
              <VStack>
                <DataPair label={tProfile("totalLoads")} value="0" />
                <DataPair label={tProfile("activeTrips")} value="0" />
                <DataPair label={tProfile("savedAddresses")} value="0" />
                <DataPair label={tProfile("contactsCount")} value="0" />
              </VStack>
            </Card>
          </VStack>

          {/* Right column - Personal Info */}
          <VStack
            gap="xl"
            data-testid="profile-settings"
            className="xl:col-span-2"
          >
            {/* Personal Information */}
            <ProfileSection
              testId="profile-personal-section"
              title={tProfile("personalInfo")}
              action={
                <Button
                  onClick={handleToggleEdit}
                  variant="ghost"
                  className="text-sm"
                >
                  {isEditing ? tCommon("cancel") : tCommon("edit")}
                  {isEditing && form.isDirty ? <DirtyIndicator /> : null}
                </Button>
              }
            >
              <FormGrid cols={isCompact || isRegular ? "none" : "sm"} gap="lg">
                <VStack gap="none">
                  <FormLabel htmlFor="full-name">
                    {tProfile("fullName")}
                  </FormLabel>
                  <InputWrapper>
                    <Input
                      id="full-name"
                      type="text"
                      value={form.fields.name}
                      onChange={(e) => form.setField("name", e.target.value)}
                      disabled={!isEditing}
                    />
                  </InputWrapper>
                </VStack>
                <VStack gap="none">
                  <FormLabel htmlFor="email">
                    {tProfile("emailAddress")}
                  </FormLabel>
                  <InputWrapper>
                    <Input
                      id="email"
                      type="email"
                      defaultValue={user.email}
                      disabled
                    />
                  </InputWrapper>
                </VStack>
                <VStack gap="none">
                  <FormLabel htmlFor="phone">
                    {tProfile("phoneNumber")}
                  </FormLabel>
                  <InputWrapper>
                    <Input
                      id="phone"
                      type="tel"
                      value={form.fields.phone}
                      onChange={(e) => form.setField("phone", e.target.value)}
                      placeholder={tProfile("phoneNumberPlaceholder")}
                      disabled={!isEditing}
                    />
                  </InputWrapper>
                </VStack>
              </FormGrid>
              {isEditing &&
              (form.serverErrors._form || form.status === "error") ? (
                <Text
                  color="danger"
                  size="sm"
                  className="mt-4"
                  data-testid="profile-save-error"
                >
                  {form.serverErrors._form ?? tCommon("error")}
                </Text>
              ) : null}
              {isEditing && (
                <HStack justify="end" className="mt-6">
                  <Button
                    onClick={handleSave}
                    disabled={!canSave}
                    data-testid="profile-save-button"
                  >
                    {tCommon("save")}
                  </Button>
                </HStack>
              )}
            </ProfileSection>

            {/* Company Information */}
            {user.entity && (
              <ProfileSection
                testId="profile-company-section"
                title={tProfile("companyInfo")}
              >
                <FormGrid cols={isCompact ? "none" : "sm"} gap="lg">
                  <VStack gap="none">
                    <FormLabel htmlFor="company-name">
                      {tProfile("companyName")}
                    </FormLabel>
                    <InputWrapper>
                      <Input
                        id="company-name"
                        type="text"
                        defaultValue={user.entity.name}
                        disabled
                      />
                    </InputWrapper>
                  </VStack>
                </FormGrid>
                <Text color="muted" className="mt-4">
                  {tProfile("contactSupportToUpdate")}
                </Text>
              </ProfileSection>
            )}
          </VStack>
        </div>
      </SettingsPageLayout>

      <ConfirmDialog
        open={pendingDiscard !== null}
        title={tCommon("discardChanges")}
        description={tCommon("confirmCancel")}
        confirmLabel={tCommon("discard")}
        cancelLabel={tCommon("keepEditing")}
        onConfirm={handleDiscardConfirm}
        onCancel={() => setPendingDiscard(null)}
      />
    </VStack>
  );
}

export const Route = createFileRoute("/_app/account/profile")({
  component: ProfilePage,
});
